import * as React from 'react';
import { BrandLockup, HeroIllustration, Icon, LogoMark, SeltzerArt } from './brand';
import { Link } from './Shell';

// app.beefedupit.com/ - the public home page. Explains BeefedUp Apps without requiring sign-in.

const Footer: React.FunctionComponent = () => (
  <footer className="bu-footer">
    <div className="bu-footer-inner">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}><LogoMark size={22} /> &copy; {new Date().getFullYear()} BeefedUp IT. BeefedUp Apps.</span>
      <span>
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
        <a href="https://beefedupit.com">beefedupit.com</a>
      </span>
    </div>
  </footer>
);

const Landing: React.FunctionComponent<{ signedIn: boolean }> = ({ signedIn }) => (
  <div>
    <header className="bu-land-nav">
      <div className="bu-topbar-inner">
        <BrandLockup size={34} />
        <span className="bu-spacer" />
        {signedIn
          ? <Link to="/apps" className="bu-btn bu-btn-primary">Open my apps <Icon name="arrow" size={16} /></Link>
          : <Link to="/signin" className="bu-btn bu-btn-secondary" style={{ background: 'transparent', color: '#fff', borderColor: '#454a52' }}>Sign in</Link>}
      </div>
    </header>

    <section className="bu-hero">
      <div className="bu-hero-grid" />
      <div className="bu-hero-inner">
        <div>
          <span className="bu-pill"><i>&#10003;</i> Business apps by BeefedUp IT</span>
          <h1>Your operations, <em>beefed up.</em></h1>
          <p>
            BeefedUp Apps is a secure workspace of business applications that BeefedUp IT builds and runs for its clients.
            Sign in with your Microsoft 365 or Google Workspace account and get the tools your team needs, with your company's data kept private to your company.
          </p>
          <div className="bu-hero-cta">
            <Link to={signedIn ? '/apps' : '/signin'} className="bu-btn bu-btn-primary bu-btn-lg">{signedIn ? 'Open my apps' : 'Sign in to your workspace'} <Icon name="arrow" size={18} /></Link>
            <a href="#apps" className="bu-btn bu-btn-lg" style={{ color: '#fff', border: '1px solid #454a52' }}>See the apps</a>
          </div>
        </div>
        <div className="bu-hero-art"><HeroIllustration /></div>
      </div>
    </section>

    <section className="bu-band" id="apps">
      <div className="bu-showcase">
        <div className="bu-card" style={{ overflow: 'hidden' }}>
          <div className="bu-app-art" style={{ height: 260 }}><SeltzerArt height={190} /></div>
          <div className="bu-card-pad" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <span className="bu-chip">Beverages</span><span className="bu-chip">Gummies</span><span className="bu-chip">Tablets &amp; capsules</span><span className="bu-chip">Powders</span><span className="bu-chip">Lot tracking</span><span className="bu-chip">Batch records</span>
          </div>
        </div>
        <div>
          <p className="bu-eyebrow">Featured app</p>
          <div className="bu-band-head" style={{ marginBottom: 0 }}>
            <h2>BatchWorks</h2>
            <p>Manufacturing production software for beverages, gummies, tablets, capsules and powders: scale a per-unit recipe to a full run, plan the tanks, kettles or blenders, and keep inventory accurate down to the lot.</p>
          </div>
          <ul>
            <li><Icon name="flask" /><span><strong>Formula library.</strong> Version-controlled recipes with per-flavor runs, active dosing and a step-by-step process guide.</span></li>
            <li><Icon name="layers" /><span><strong>Lot-tracked inventory.</strong> Receive ingredients and packaging by supplier lot and expiry. Completing a batch deducts stock automatically, earliest expiry first.</span></li>
            <li><Icon name="file" /><span><strong>Batch records.</strong> Branded PDF batch sheets listing every lot used, for full traceability.</span></li>
            <li><Icon name="users" /><span><strong>Roles.</strong> Operators run batches; admins own formulas, stock and corrections.</span></li>
          </ul>
        </div>
      </div>
    </section>

    <section className="bu-dark-band">
      <div className="bu-band">
        <div className="bu-band-head">
          <p className="bu-eyebrow" style={{ color: '#ff8589' }}>Security by design</p>
          <h2>Each company's data stays its own</h2>
          <p>BeefedUp Apps serves many organizations from one platform, but every company's data is walled off from every other, and the database itself enforces it.</p>
        </div>
        <div className="bu-trust">
          <div><Icon name="key" size={24} /><h3>Sign in with your work account</h3><p>Microsoft 365 or Google Workspace. No new passwords; your IT team stays in control of who has access.</p></div>
          <div><Icon name="database" size={24} /><h3>Isolated per organization</h3><p>Every record is tagged to your organization and filtered by database-level row security on every request.</p></div>
          <div><Icon name="shield" size={24} /><h3>Hosted on Microsoft Azure</h3><p>Encrypted in transit and at rest, with automatic backups and point-in-time restore.</p></div>
          <div><Icon name="users" size={24} /><h3>Your admins, your rules</h3><p>Your own administrators decide who can edit formulas, adjust stock and manage the team.</p></div>
        </div>
      </div>
    </section>

    <section className="bu-band">
      <div className="bu-features">
        <div className="bu-card bu-feature"><div className="bu-feature-icon"><Icon name="grid" /></div><h3>One place for your tools</h3><p>Every app your company uses from BeefedUp IT, in a single launcher. You only see what your organization has enabled.</p></div>
        <div className="bu-card bu-feature"><div className="bu-feature-icon"><Icon name="spark" /></div><h3>Built for your workflow</h3><p>Apps designed around how your team actually works, from production floor to office, on desktop or phone.</p></div>
        <div className="bu-card bu-feature"><div className="bu-feature-icon"><Icon name="building" /></div><h3>Works in Teams too</h3><p>Microsoft 365 organizations can pin apps in SharePoint or Microsoft Teams, all using the same data.</p></div>
      </div>
      <div style={{ marginTop: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }} className="bu-card bu-card-pad">
        <div>
          <h3 className="bu-card-title" style={{ fontSize: 20 }}>Already a BeefedUp IT client?</h3>
          <p style={{ margin: '6px 0 0', color: 'var(--text-2)' }}>Sign in with your work account to open your apps. New to BeefedUp IT? <a href="https://beefedupit.com">Get in touch</a>.</p>
        </div>
        <Link to={signedIn ? '/apps' : '/signin'} className="bu-btn bu-btn-dark bu-btn-lg">{signedIn ? 'Open my apps' : 'Sign in'} <Icon name="arrow" size={18} /></Link>
      </div>
    </section>
    <Footer />
  </div>
);

export default Landing;
export { Footer };
